import React, { useState, useRef, useEffect } from 'react';
import { Check, X, Loader } from 'lucide-react';

/**
 * Inline Edit Field Component
 * Click to edit, save on blur or Enter, cancel on Escape
 *
 * @param {string} value - Current field value
 * @param {function} onSave - Callback when value is saved (value) => Promise
 * @param {string} type - Input type: 'text', 'textarea', 'email', 'tel', 'date', 'select'
 * @param {string} placeholder - Placeholder text
 * @param {Array} options - Options for select type [{value, label}]
 * @param {string} className - Additional CSS classes
 * @param {boolean} multiline - Use textarea instead of input
 */
const InlineEditField = ({
    value,
    onSave,
    type = 'text',
    placeholder = 'Click to edit',
    options = [],
    className = '',
    multiline = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        setEditValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            // Select all text for easy replacement
            if (inputRef.current.select) {
                inputRef.current.select();
            }
        }
    }, [isEditing]);

    const handleClick = () => {
        if (!isEditing) {
            setIsEditing(true);
            setError(null);
        }
    };

    const handleSave = async () => {
        // Don't save if value hasn't changed
        if (editValue === value) {
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            await onSave(editValue);
            setIsEditing(false);
        } catch (err) {
            setError(err.message || 'Failed to save');
            console.error('Save error:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
        setError(null);
    };

    const handleBlur = () => {
        // Save on blur
        if (isEditing && !isSaving) {
            handleSave();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !multiline) {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    // Rendering logic based on state
    if (isEditing) {
        if (type === 'select') {
            return (
                <div className="relative inline-block w-full">
                    <select
                        ref={inputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        disabled={isSaving}
                        className={`w-full px-2 py-1 border-2 border-green-500 rounded focus:outline-none focus:ring-2 focus:ring-green-400 ${className}`}
                    >
                        <option value="">Select...</option>
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {isSaving && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            <Loader className="animate-spin text-green-600" size={16} />
                        </div>
                    )}
                    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
                </div>
            );
        }

        if (multiline) {
            return (
                <div className="relative w-full">
                    <textarea
                        ref={inputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        disabled={isSaving}
                        rows={3}
                        placeholder={placeholder}
                        className={`w-full px-2 py-1 border-2 border-green-500 rounded focus:outline-none focus:ring-2 focus:ring-green-400 resize-none ${className}`}
                    />
                    {isSaving && (
                        <div className="absolute right-2 top-2">
                            <Loader className="animate-spin text-green-600" size={16} />
                        </div>
                    )}
                    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
                </div>
            );
        }

        return (
            <div className="relative inline-block w-full">
                <input
                    ref={inputRef}
                    type={type}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isSaving}
                    placeholder={placeholder}
                    className={`w-full px-2 py-1 border-2 border-green-500 rounded focus:outline-none focus:ring-2 focus:ring-green-400 ${className}`}
                />
                {isSaving && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Loader className="animate-spin text-green-600" size={16} />
                    </div>
                )}
                {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
            </div>
        );
    }

    // Display mode
    const displayValue = value || placeholder;
    const isPlaceholder = !value;

    return (
        <div
            onClick={handleClick}
            className={`cursor-pointer px-2 py-1 rounded hover:bg-gray-100 transition-colors ${
                isPlaceholder ? 'text-gray-400 italic' : 'text-gray-900'
            } ${className}`}
            title="Click to edit"
        >
            {displayValue}
        </div>
    );
};

export default InlineEditField;
